'use client'

import { STUDIO_ARRIVE_EARLY_MINUTES } from '@repo/core/buffers'

import { ChoiceChip } from '@/components/molecules/choice-chip.client'
import { DayPicker } from '@/components/molecules/date-picker.client'
import { QuantityStepper } from '@/components/molecules/quantity-stepper.client'
import { FootageField } from '@/components/sections/creator/footage-field.client'
import { LengthSlider } from '@/components/sections/creator/length-slider.client'
import { FromFilmField } from '@/components/sections/order/from-film-field.client'
import { HourPicker } from '@/components/sections/order/hour-picker.client'
import { OrderBriefField } from '@/components/sections/order/order-brief-field.client'
import { OrderField } from '@/components/sections/order/order-field.client'
import { SourceLinkField } from '@/components/sections/order/source-link-field.client'
import {
  bookedMinutes,
  type CreatorCityView,
  type CreatorOfferingView,
  type CreatorPick,
  formatMinutes,
  hasQuantity,
  isConfiguredShoot,
  needsBrief,
  needsCity,
  needsDate,
  needsFootage,
  needsLengthBand,
  needsRange,
  needsSource,
  needsTime,
  type PickField,
  pickDays,
  quantityHint,
  startChoices,
} from '@/lib/creator'
import { formatDay } from '@/lib/dates'
import { dayWord, lastHeldDay, returnDay } from '@/lib/rental'
import { pluralize } from '@/lib/tiers'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * When, where and how many — for anything on offer.
 *
 * Every control here is decided by a CAPABILITY COLUMN and nothing else:
 * `bookingMode` says whether a calendar appears and of what shape,
 * `coverageMode` whether a city has to be named, and a hire is the one thing
 * with no quantity because the term is its identity. There is no branch on "is
 * this a camera" anywhere below, which is why a new kind of offer costs no code
 * here at all.
 *
 * ── PRESS, DON'T PICK ────────────────────────────────────────────────────
 * Everything with a small, known set of answers is a button or a slider. The
 * hour was a dropdown over the eight starts a room publishes; the city was a
 * dropdown over two; the footage was a number box with no sense of what the
 * allowance was. Each one hid a list that fits on screen behind a click, which
 * is the difference between reading your options and going to look for them.
 * What is left as typing is what genuinely has no set of answers: a paragraph
 * and a link.
 *
 * ── EVERY REFUSAL SITS ON ITS OWN FIELD ──────────────────────────────────
 * `pickIssue` names the control a complaint belongs to, and `OrderField` prints
 * it there. One amber sentence at the foot of the card used to have to cover
 * six controls, so "Wybierz datę." was a sentence the reader had to match to a
 * field themselves.
 */

const FIELD_GRID = 'mt-5 grid grid-cols-1 gap-5 border-t border-white/10 pt-5 sm:grid-cols-2'

export function OrderLineFields({
  offering,
  pick,
  cities,
  films,
  lineId,
  problemField,
  problem,
  onPatch,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  cities: CreatorCityView[]
  /**
   * The other films in this order (spec §8 · G8) — empty for every basket that
   * has none, which is what keeps the extra control off almost every line.
   */
  films: { lineId: string; label: string }[]
  /** Scopes the control ids: the same offering can be in the order twice. */
  lineId: string
  /** Which control the line's one refusal belongs to, or `null` when it is ready. */
  problemField: PickField | null
  problem: string | null
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  const covered = cities.filter((c) => offering.citySlugs.includes(c.slug))
  const hint = quantityHint(offering, pick)
  // `startChoices`, not `startSlots`: a room's hours come from its published
  // calendar and a shoot plan's from the plan itself, and this control draws
  // both. Splitting that decision between the component and the validator is
  // what once let the picker offer an hour `pickIssue` then refused.
  const slots = needsTime(offering, pick) ? [...startChoices(offering, pick, pick.from)] : []

  /** The sentence, but only on the control it is about. */
  const at = (field: PickField) => (problemField === field ? problem : null)

  const days = pickDays(offering, pick)
  const back = needsRange(offering) ? returnDay(pick.from, days) : ''

  return (
    <div className={FIELD_GRID}>
      {needsRange(offering) ? (
        // ONE date, not two. A hire's length is its variant (spec §11 · I4) and
        // `refFrom` derives the far end from that term — so the range picker was
        // asking the customer to choose a return date that was then thrown away.
        // The term is chosen above, the pick-up here, and the return is stated.
        <OrderField
          wide
          label="Kiedy odbierasz sprzęt?"
          problem={at('date')}
          hint={
            back
              ? `Zwrot ${formatDay(back)} do tej samej godziny — ${days} ${dayWord(days)} w Twoich rękach.`
              : 'Wybierz dzień odbioru, a policzymy zwrot z długości najmu wybranej wyżej.'
          }
        >
          <DayPicker
            id={`termin-${lineId}`}
            value={pick.from}
            placeholder="Wybierz dzień odbioru"
            // `to` follows the TERM, and is written here so the stored row says
            // the same thing `refFrom` will derive from it at checkout.
            onChange={(from) => onPatch({ from, to: lastHeldDay(from, days) })}
          />
        </OrderField>
      ) : needsDate(offering) ? (
        <OrderField
          label={needsTime(offering, pick) ? 'Dzień' : 'Pierwszy dzień'}
          problem={at('date')}
          {...(offering.bookingMode === 'DAY' && pick.qty > 1
            ? { hint: `${pick.qty} ${dayWord(pick.qty)} z rzędu, licząc od tego dnia.` }
            : {})}
        >
          <DayPicker
            id={`termin-${lineId}`}
            value={pick.from}
            onChange={(from) => onPatch({ from })}
          />
        </OrderField>
      ) : null}

      {/* Spec §10 · I1: whole hours, from the calendar the office actually
          published, and only the ones the whole booking still fits inside.
          This used to be a hardcoded 08:00–20:00 half-hour list; then a
          dropdown over the real ones; now the real ones, on screen. */}
      {needsTime(offering, pick) ? (
        <OrderField
          label="Od godziny"
          problem={at('time')}
          hint={`Blok trwa ${formatMinutes(bookedMinutes(offering, pick))}. Bądź ${STUDIO_ARRIVE_EARLY_MINUTES} minut przed startem — godzinę przed i po trzymamy wolną na przygotowanie sali.`}
        >
          <HourPicker
            slots={slots}
            value={pick.time}
            minutes={bookedMinutes(offering, pick)}
            hasDay={Boolean(pick.from)}
            onChange={(time) => onPatch({ time })}
          />
        </OrderField>
      ) : null}

      {needsCity(offering) ? (
        <OrderField label="Miasto" problem={at('city')}>
          <div className="flex flex-wrap gap-2">
            {covered.map((city) => (
              <ChoiceChip
                key={city.slug}
                pressed={pick.citySlug === city.slug}
                onClick={() => onPatch({ citySlug: city.slug })}
              >
                {city.name}
              </ChoiceChip>
            ))}
          </div>
        </OrderField>
      ) : null}

      {/* No day stepper on a configured shoot: its price is the whole session —
          the plan, the people and the photos — so „2 dni" here would sell the
          same session twice. Two shoot days are two lines, configured on
          `/pl/fotograf-operator` the way the first one was. */}
      {hasQuantity(offering) && !isConfiguredShoot(offering, pick) ? (
        // No hint: `quantityHint` is already inside the control, on the right of
        // the stepper. Repeating it underneath printed "2 h" twice, four lines
        // apart, as if they were two different facts.
        <OrderField htmlFor={`qty-${lineId}`} label={offering.quantityLabel} problem={at('qty')}>
          <div className="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-white/15 bg-black/40 px-3">
            <QuantityStepper
              id={`qty-${lineId}`}
              value={pick.qty}
              onChange={(qty) => onPatch({ qty })}
              suffix={pluralize(pick.qty, offering.unitForms)}
            />
            {hint ? (
              <span className={cn(META, 'shrink-0 font-semibold text-white/55')}>{hint}</span>
            ) : null}
          </div>
        </OrderField>
      ) : null}

      {/* Before the brief, because the length is the thing being PRICED — the
          number on the rail moves when this moves, and a control that changes
          the total belongs above the one that describes the work. */}
      {needsLengthBand(offering) ? (
        <LengthSlider
          offering={offering}
          pick={pick}
          lineId={lineId}
          problem={at('length')}
          onPatch={onPatch}
        />
      ) : null}

      {/* Last of the quick answers, and full width. Spec §6 asks for a
          paragraph, not a chip: it is the one control here that is read rather
          than clicked, and putting it above the date would push every quick
          choice below the fold. */}
      {needsBrief(offering) ? (
        <OrderBriefField
          offering={offering}
          pick={pick}
          lineId={lineId}
          problem={at('brief')}
          onPatch={onPatch}
        />
      ) : null}

      {/* Above the link, because it DECIDES the link: a reel cut from a film in
          this same basket brings no tape of its own (spec §8 · G8), so the two
          rows below it appear or vanish on this answer. Only drawn when there
          is a film here to point at — which is almost never. */}
      {films.length > 0 &&
      offering.sourceMode === 'REQUIRED' &&
      offering.pricingUnit !== 'MINUTE' ? (
        <FromFilmField pick={pick} films={films} onPatch={onPatch} />
      ) : null}

      {/* Under the brief, because that is the order the two are answered in:
          first what to make, then what to make it from. */}
      {needsSource(offering, pick) ? (
        <SourceLinkField
          offering={offering}
          pick={pick}
          lineId={lineId}
          problem={at('source')}
          onPatch={onPatch}
        />
      ) : null}

      {/* Right under the link, because they are one question asked twice:
          where the tape is, and how much of it there is. */}
      {needsFootage(offering, pick) ? (
        <FootageField
          offering={offering}
          pick={pick}
          lineId={lineId}
          problem={at('footage')}
          onPatch={onPatch}
        />
      ) : null}
    </div>
  )
}
